import React from "react";
import { useTranslation } from "react-i18next";
import { Crown, Footprints, Repeat, ShoppingBag, Truck, UserPlus, Users, Wallet } from "lucide-react";
import { reportsAPI } from "@/lib/api";
import { useCurrency } from "@/hooks/useCurrency";
import { usePermission } from "@/hooks/usePermission";
import { ReportTable } from "../components/ReportTable";
import { RankedBars } from "../components/RankedBars";
import { StatTile } from "../components/StatTile";
import { ReportNote, ReportState, StatGrid, useReportQuery } from "../components/ReportBlocks";
import { formatPercent, num, personName, share, type ReportColumn } from "../lib/columns";
import type { ReportBodyProps } from "../types";

const sumBy = <T,>(rows: T[], pick: (row: T) => number) => rows.reduce((s, r) => s + pick(r), 0);

/** Customers ranked: enough for a meaningful list without loading the whole customer base. */
const TOP_CUSTOMERS_LIMIT = 100;

/* ─────────────────────────────── Top customers ─────────────────────────────── */

export function TopCustomersReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const canCustomers = usePermission("customers.read");
	const query = useReportQuery(
		"top-customers",
		params,
		() => reportsAPI.getTopCustomers({ ...params, limit: TOP_CUSTOMERS_LIMIT }) as Promise<any[]>,
	);
	return (
		<ReportState query={query}>
			{data => {
				const all = Array.isArray(data) ? data : [];
				// Sales without a customer come back as one unnamed row: shown as walk-in, kept out of the ranking.
				const walkIn = all.find(r => r.customerId == null);
				const rows = all.filter(r => r.customerId != null);
				const named = sumBy(rows, r => num(r.totalSpent));
				const everything = named + num(walkIn?.totalSpent);
				const repeat = rows.filter(r => num(r.totalOrders) > 1).length;
				const href = canCustomers ? (r: any) => `/contacts?type=customers&edit=${r.customerId}` : undefined;
				const columns: ReportColumn<any>[] = [
					{
						key: "name",
						label: t("reports.cols.customer"),
						value: r => r.customerName || `#${r.customerId}`,
					},
					{
						key: "phone",
						label: t("reports.cols.phone"),
						value: r => r.customerPhone || "",
						render: r => <span dir="ltr">{r.customerPhone || "-"}</span>,
						hideOnMobile: true,
					},
					{
						key: "orders",
						label: t("reports.cols.orders"),
						value: r => num(r.totalOrders),
						kind: "number",
						total: true,
					},
					{
						key: "avg",
						label: t("reports.cols.avgOrder"),
						value: r => num(r.averageOrderValue),
						kind: "money",
						currency,
						hideOnMobile: true,
					},
					{
						key: "spent",
						label: t("reports.cols.totalSpent"),
						value: r => num(r.totalSpent),
						kind: "money",
						currency,
						total: true,
					},
					{
						key: "share",
						label: t("reports.cols.share"),
						value: r => share(num(r.totalSpent), everything),
						kind: "percent",
						hideOnMobile: true,
					},
				];
				return (
					<div className="space-y-4">
						<StatGrid>
							<StatTile
								icon={Users}
								label={t("reports.kpi.payingCustomers")}
								value={rows.length}
								hint={
									rows.length >= TOP_CUSTOMERS_LIMIT
										? t("reports.kpi.topN", { count: TOP_CUSTOMERS_LIMIT })
										: undefined
								}
							/>
							<StatTile
								icon={Wallet}
								label={t("reports.kpi.knownCustomerSales")}
								value={named}
								format="money"
								currency={currency}
								hint={t("reports.kpi.shareOfSales", { value: formatPercent(share(named, everything)) })}
							/>
							<StatTile
								icon={Repeat}
								label={t("reports.kpi.repeatCustomers")}
								value={repeat}
								hint={t("reports.kpi.ofTotal", { count: rows.length })}
							/>
							<StatTile
								icon={Footprints}
								label={t("reports.kpi.walkInSales")}
								value={num(walkIn?.totalSpent)}
								format="money"
								currency={currency}
								hint={t("reports.salesTrend.invoicesCount", { count: num(walkIn?.totalOrders) })}
							/>
						</StatGrid>
						<RankedBars
							title={t("reports.charts.topCustomers")}
							currency={currency}
							items={rows.map(r => ({
								key: r.customerId,
								label: r.customerName || `#${r.customerId}`,
								value: num(r.totalSpent),
								sub: t("reports.salesTrend.invoicesCount", { count: num(r.totalOrders) }),
								href: href?.(r),
							}))}
						/>
						<ReportTable
							columns={columns}
							rows={rows}
							rowKey={r => r.customerId}
							rowHref={href}
							exportName={exportName}
							emptyIcon={Crown}
							defaultSort={{ key: "spent", dir: "desc" }}
						/>
						<ReportNote>{t("reports.notes.paidSales")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}

/* ─────────────────────────────── New customers ─────────────────────────────── */

export function NewCustomersReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const canCustomers = usePermission("customers.read");
	const query = useReportQuery(
		"new-customers",
		params,
		() => reportsAPI.getCustomersReport(params) as Promise<any[]>,
	);
	return (
		<ReportState query={query}>
			{data => {
				const rows = Array.isArray(data) ? data : [];
				const buyers = rows.filter(c => num(c.totalSales) > 0);
				const href = canCustomers ? (c: any) => `/contacts?type=customers&edit=${c.id}` : undefined;
				const columns: ReportColumn<any>[] = [
					{ key: "name", label: t("reports.cols.customer"), value: c => personName(c) || `#${c.id}` },
					{
						key: "number",
						label: t("reports.cols.number"),
						value: c => c.customerNumber || "",
						hideOnMobile: true,
					},
					{
						key: "phone",
						label: t("reports.cols.phone"),
						value: c => c.phone || "",
						render: c => <span dir="ltr">{c.phone || "-"}</span>,
					},
					{
						key: "branch",
						label: t("reports.cols.branch"),
						value: c => c.branch?.name || "",
						hideOnMobile: true,
					},
					{
						key: "joined",
						label: t("reports.cols.joined"),
						value: c => c.joinDate || c.createdAt,
						kind: "date",
					},
					{
						key: "orders",
						label: t("reports.cols.lifetimeOrders"),
						value: c => num(c.totalSales),
						kind: "number",
						total: true,
						hideOnMobile: true,
					},
					{
						key: "spent",
						label: t("reports.cols.lifetimeSpent"),
						value: c => num(c.totalSpent),
						kind: "money",
						currency,
						total: true,
					},
				];
				return (
					<div className="space-y-4">
						<StatGrid>
							<StatTile icon={UserPlus} label={t("reports.kpi.newCustomers")} value={rows.length} />
							<StatTile
								icon={ShoppingBag}
								label={t("reports.kpi.alreadyBought")}
								value={buyers.length}
								hint={t("reports.kpi.conversion", {
									value: formatPercent(share(buyers.length, rows.length)),
								})}
							/>
							<StatTile
								icon={Wallet}
								label={t("reports.kpi.newCustomerSpend")}
								value={sumBy(rows, c => num(c.totalSpent))}
								format="money"
								currency={currency}
							/>
							<StatTile
								icon={Repeat}
								label={t("reports.kpi.repeatCustomers")}
								value={rows.filter(c => num(c.totalSales) > 1).length}
							/>
						</StatGrid>
						<ReportTable
							columns={columns}
							rows={rows}
							rowKey={c => c.id}
							rowHref={href}
							exportName={exportName}
							emptyIcon={UserPlus}
							defaultSort={{ key: "joined", dir: "desc" }}
						/>
						<ReportNote>{t("reports.notes.newContacts")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}

/* ─────────────────────────────── New suppliers ─────────────────────────────── */

export function NewSuppliersReport({ params, exportName }: ReportBodyProps) {
	const { t } = useTranslation();
	const currency = useCurrency();
	const canSuppliers = usePermission("suppliers.read");
	const query = useReportQuery(
		"new-suppliers",
		params,
		() => reportsAPI.getSuppliersReport(params) as Promise<any[]>,
	);
	return (
		<ReportState query={query} tiles={2}>
			{data => {
				const rows = Array.isArray(data) ? data : [];
				const href = canSuppliers ? (s: any) => `/contacts?type=suppliers&edit=${s.id}` : undefined;
				const columns: ReportColumn<any>[] = [
					{ key: "name", label: t("reports.cols.supplier"), value: s => s.name || `#${s.id}` },
					{
						key: "phone",
						label: t("reports.cols.phone"),
						value: s => s.phone || "",
						render: s => <span dir="ltr">{s.phone || "-"}</span>,
					},
					{ key: "email", label: t("reports.cols.email"), value: s => s.email || "", hideOnMobile: true },
					{
						key: "joined",
						label: t("reports.cols.joined"),
						value: s => s.joinDate || s.createdAt,
						kind: "date",
					},
					{
						key: "orders",
						label: t("reports.cols.lifetimePurchases"),
						value: s => num(s.totalPurchases),
						kind: "number",
						total: true,
						hideOnMobile: true,
					},
					{
						key: "amount",
						label: t("reports.cols.lifetimeAmount"),
						value: s => num(s.totalAmount),
						kind: "money",
						currency,
						total: true,
					},
				];
				return (
					<div className="space-y-4">
						<StatGrid className="xl:grid-cols-2">
							<StatTile icon={Truck} label={t("reports.kpi.newSuppliers")} value={rows.length} />
							<StatTile
								icon={Wallet}
								label={t("reports.kpi.purchasedFromThem")}
								value={sumBy(rows, s => num(s.totalAmount))}
								format="money"
								currency={currency}
							/>
						</StatGrid>
						<ReportTable
							columns={columns}
							rows={rows}
							rowKey={s => s.id}
							rowHref={href}
							exportName={exportName}
							emptyIcon={Truck}
							defaultSort={{ key: "joined", dir: "desc" }}
						/>
						<ReportNote>{t("reports.notes.newContacts")}</ReportNote>
					</div>
				);
			}}
		</ReportState>
	);
}
